import chroma from 'chroma-js';
import { type PageModule, PageModuleType } from '@/types/module.types';
import type { BrandliveEvent } from '@/types/projects.types';
import type { LanguagesAbbr } from '@/types/translation.types';
import type { ICustomDesignItem } from '@/types/theme-packs.types';
import { getUseableFontFamily } from '@/micros/edit-project/grid-areas/main/components/theme-pack-font-variant-change/utils/get-useable-font-family';

export const BRANDLIVE_MAILING_ADDRESS = 'Sent by Brandlive Inc., 3303 N Mississippi Ave Ste 200, Portland, OR 97227';
// the footer card's look - the preset for every template's own footer (keyed by BLOCK_ID.mailingAddress)
// and the seeded style of one added from the palette. Mirrored in events-api's template-utils.ts.
// no colors - each template supplies its own through the block context (footerTextColor, footerBackground)
export const FOOTER_CARD_STYLE = {
  padding: '32px 32px',
  margin: '40px',
  align: 'left',
} as const;
// recolored variants (alpha/shape untouched) of the Brandlive wordmark, per-template where the
// default gray asset has poor contrast or doesn't match the template's spec
export const BRANDLIVE_LOGO_WHITE = 'https://assets.brandlive.com/assets/images/emails/brandlive-logo-white.png';
export const BRANDLIVE_LOGO_2F2F2F = 'https://assets.brandlive.com/assets/images/emails/brandlive-logo-2f2f2f.png';
export const DEFAULT_LOGO = 'https://assets.brandlive.com/assets/images/emails/default-logo-pinwheel.png';
export const PINWHEEL_LOGO_DARK = 'https://assets.brandlive.com/assets/images/emails/default-logo-pinwheel-dark.png';
export const PINWHEEL_LOGO_D6D5C9 =
  'https://assets.brandlive.com/assets/images/emails/default-logo-pinwheel-d6d5c9.png';
export const BRANDTV_LOGO = 'https://assets.brandlive.com/assets/images/emails/default-logo-brandtv.png';
export const CHATEAU_LOGO = 'https://assets.brandlive.com/assets/images/emails/default-logo-w.png';
export const LAURENT_LOGO = 'https://assets.brandlive.com/assets/images/emails/default-logo-circle.png';
export const MCMXCIV_LOGO = 'https://assets.brandlive.com/assets/images/emails/default-logo-MCMXCIV.png';
export const MCMXCIV_LOGO_D4A847 = 'https://assets.brandlive.com/assets/images/emails/default-logo-MCMXCIV-d4a847.png';

export type ParseColorOptions = {
  opacity?: number;
};
export function parseColor([hex, opacity]: [string, number], opts?: ParseColorOptions): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const color = `rgba(${r},${g},${b},${opts?.opacity ?? opacity})`;
  return color;
}

export function resolveColor(themeColor: [string, number], customHex?: string): string {
  if (customHex) return customHex;
  return parseColor(themeColor);
}

/**
 * per-email override wins, otherwise fall back to the channel-level theme color,
 * otherwise fall back to the templates own hardcoded design default
 */
export function resolveThemedColor(
  override: string | undefined,
  themeColor: [string, number] | undefined,
  fallback: string | undefined
): string {
  if (override) return override;
  if (themeColor) return parseColor(themeColor);
  return fallback ?? '';
}

/**
 * same precedence as resolveThemedColor, but returns a plain hex string instead of an
 * rgba() CSS value - for contexts like <input type="color"> that can't parse/display
 * rgba() and don't support an alpha channel anyway
 */
export function resolveThemedColorHex(
  override: string | undefined,
  themeColor: [string, number] | undefined,
  fallback: string | undefined
): string {
  if (override) return override;
  if (themeColor) return themeColor[0];
  return fallback ?? '';
}

/**
 * picks whichever Brandlive wordmark variant has more contrast against a given background,
 * so the logo stays legible regardless of per-template design or per-email color overrides
 */
export function getContrastingBrandliveLogo(backgroundColor: string | undefined): string {
  if (!backgroundColor || !chroma.valid(backgroundColor)) return BRANDLIVE_LOGO_2F2F2F;
  const isDark = chroma(backgroundColor).get('lab.l') < 70;
  return isDark ? BRANDLIVE_LOGO_WHITE : BRANDLIVE_LOGO_2F2F2F;
}

/**
 * returns the given color (hex or rgba/rgb string) as an rgba() string at the given opacity -
 * for tinting a background with a theme color without touching the text/content drawn on top of it
 */
export function withOpacity(color: string, opacity: number): string {
  if (!chroma.valid(color)) return color;
  const [r, g, b] = chroma(color).rgb();
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * per-email override wins, otherwise fall back to the channel-level theme font,
 * otherwise use the templates own hardcoded font stack
 */
export function resolveThemedFont(
  override: string | undefined,
  themeFontName: string | undefined,
  fallbackStack: string
): string {
  const name = override || getUseableFontFamily(themeFontName);
  if (!name) return fallbackStack;
  // keep the template's full fallback chain (not just the generic keyword) behind a
  // chosen/uploaded font - Outlook ignores CSS generic keywords and renders Times New
  // Roman if the first font isn't recognized, so a real web-safe font must stay in the chain
  return `${name}, ${fallbackStack}`;
}

/**
 * same precedence as resolveThemedFont, but returns a plain font name instead of a full
 * CSS font-family - for a <select> of specific font-name options (the editors
 * font picker) rather than a CSS property value
 */
export function resolveThemedFontName(
  override: string | undefined,
  themeFontName: string | undefined,
  fallback: string | undefined
): string {
  return override || getUseableFontFamily(themeFontName) || fallback || '';
}

/**
 * reads a border-radius from the channel-level themes per-component-type corner style
 * falls back to the templates own hardcoded design default when the theme hasn't set one
 */
export function resolveThemedRadius(themeStyle: ICustomDesignItem | undefined, fallback: string): string {
  const radius = themeStyle?.css.borderRadius;
  if (radius === undefined || radius === null || radius === '') return fallback;
  return typeof radius === 'number' ? `${radius}px` : String(radius);
}

function getMainEventBanner(module: PageModule) {
  return module.type === PageModuleType.main_event_banner;
}

function getBanner(eventBundle: BrandliveEvent) {
  if (eventBundle.settings.display_landing_page) {
    const mainBanner = eventBundle?.homepage?.modules.find(getMainEventBanner);
    if (mainBanner && mainBanner.is_on) return mainBanner;
  }

  if (eventBundle.settings.display_homepage) {
    const mainHomeBanner = eventBundle.homepage?.post_register_home_modules?.find(getMainEventBanner);
    if (mainHomeBanner?.is_on) {
      return mainHomeBanner;
    }
  }
}

export function getBannerImage(eventBundle: BrandliveEvent): string {
  if (eventBundle.registration_settings?.image) {
    return eventBundle.registration_settings.image;
  }

  if (eventBundle.event_image) {
    return eventBundle.event_image;
  }

  const banner = getBanner(eventBundle);

  if (banner) {
    return banner.content.image;
  } else {
    const session = eventBundle.sessions[0];
    return session.image || session.pre_broadcast_thumbnail || ``;
  }
}

export const titleToLines = (title: string): string => {
  const words = title.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    if (!currentLine) {
      currentLine = word;
    } else if ((currentLine + ' ' + word).length <= 12) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }

  if (currentLine) lines.push(currentLine);

  return lines.join('<br/>');
};

export function getHeaderImage(eventBundle: BrandliveEvent, fallback?: string | null): string {
  if (eventBundle.registration_settings?.logo) {
    return eventBundle.registration_settings.logo;
  }

  return fallback || '';
}

// Rough estimate: average character is ~0.5-0.6 times the font size
const charWidth = (fontSize = 16) => fontSize * 0.55;

const parse = (text: string) => {
  const div = document.createElement('div');
  div.innerHTML = text;
  return div.innerText;
};

export const description = (
  event: BrandliveEvent,
  language: LanguagesAbbr,
  maxWidth: number,
  maxLines = 3,
  fontSize = 16
) => {
  if (event.event_description) return event.event_description;

  const banner = getBanner(event);
  const reg = event.registration_settings?.description?.[language ?? 'base'];
  const sDesc = event.sessions[0]?.description?.[language ?? 'base'];
  const text = parse(banner?.content?.description?.[language ?? 'base'] ?? reg ?? sDesc);
  const maxLen = (maxWidth * maxLines) / charWidth(fontSize);
  if (text.length > maxLen) {
    return text.substring(0, text.indexOf(' ', maxLen)) + '...';
  }

  return text;
};

export const eventImage = (event: BrandliveEvent) => {
  return getBannerImage(event);
};

export const eventTitle = (event: BrandliveEvent, language: LanguagesAbbr): string => {
  const banner = getBanner(event);
  return (
    banner?.content?.title?.[language ?? 'base'] ??
    event.registration_settings?.title?.[language ?? 'base'] ??
    event.registration_settings?.title?.base ??
    ''
  );
};

export const formatTimestamp = (ts: number): string =>
  new Date(ts).toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });

export const formatDateParts = (ts: number, timezone?: string) => {
  const date = new Date(ts);

  const timeParts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const hour = timeParts.find((p) => p.type === 'hour')?.value ?? '';
  const minute = timeParts.find((p) => p.type === 'minute')?.value ?? '';
  const period = (timeParts.find((p) => p.type === 'dayPeriod')?.value ?? '').toUpperCase();

  const dateParts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const month = dateParts.find((p) => p.type === 'month')?.value ?? '';
  const day = dateParts.find((p) => p.type === 'day')?.value ?? '';

  return {
    dayName: date.toLocaleString('en-US', { timeZone: timezone, weekday: 'long' }).toUpperCase(),
    shortDayName: date.toLocaleString('en-US', { timeZone: timezone, weekday: 'short' }).toUpperCase(),
    shortDate: date.toLocaleString('en-US', { timeZone: timezone, month: 'short', day: 'numeric' }).toUpperCase(),
    numericDate: `${month}/${day}`,
    period,
    time: `${hour}:${minute}`,
  };
};

interface DateFormatOptions {
  dayFirst?: boolean;
  commaBeforeYear?: boolean;
}
export const getSessionDateLabel = (
  timestamp: number | null | undefined,
  timezone: string | undefined,
  options: Intl.DateTimeFormatOptions,
  formatOptions: DateFormatOptions = { dayFirst: false, commaBeforeYear: true } // for day -> month -> year rather than default month -> day -> year
): string | null => {
  if (timestamp) {
    const date = new Date(timestamp);
    const locale = formatOptions.dayFirst ? 'en-GB' : 'en-US';
    const formatted = date.toLocaleDateString(locale, { ...options, timeZone: timezone });
    if (formatOptions.dayFirst && formatOptions.commaBeforeYear) {
      return formatted.replace(/( \d\d\d\d)$/, ',$1');
    }
    return formatted;
  }

  return null;
};

export const getSessionTimeLabel = (
  timestamp: number | null | undefined,
  timezone: string | undefined,
  options?: Intl.DateTimeFormatOptions
): string | null =>
  timestamp
    ? new Date(timestamp).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
        ...options,
        timeZone: timezone,
      })
    : null;
